import { WarningOutlined } from '@ant-design/icons';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { LoginCallback, Security } from '@okta/okta-react';
import { Alert, Col, Layout, Row, Space, App as AppContext } from 'antd';
import ErrorBoundary from 'antd/lib/alert/ErrorBoundary';
import { useCallback } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { errorMessage } from '../../lib/utils/messages';
import oktaAuthService from '../../okta';
import Account from '../Account';
import Home from '../Home';
import PersonnelInfo from '../PersonnelInfo';
import PersonnelSearch from '../PersonnelSearch';
import AllTeams from '../Teams/AllTeams';
import TeamEditor from '../Teams/TeamEditor';
import TeamRoster from '../Teams/TeamRoster';
import TeamSettings from '../Teams/TeamSettings';
import Feedback from '../Feedback';
import ExportData from '../ExportData';
import RoutedAppHeader from './RoutedAppHeader';
import routes from './routes';
import { SecureRoutes } from '../SecureRoutes';
import DailyRosterViewer from '../DailyRoster/DailyRosterViewer';
import PersonalRoster from '../PersonalRoster';

const { Content } = Layout;

const ErrorBoundaryMessage = () => {
  return (
    <Space style={{ alignItems: 'baseline' }}>
      <WarningOutlined />
      <p>{errorMessage.errorBoundaryMessage}</p>
    </Space>
  );
};

const LoginError = () => {
  return (
    <LoginCallback
      errorComponent={(e) => {
        if (
          e.error.message.startsWith(
            'Could not load PKCE codeVerifier from storage.'
          ) ||
          e.error.message.startsWith(
            "OAuth flow response state doesn't match request state"
          )
        ) {
          window.location.replace(routes.home);
          return null;
        }
        return (
          <Alert
            message={e.error.message}
            description={errorMessage.loginErrorDescription}
            type="error"
            showIcon
          />
        );
      }}
    />
  );
};

/**
 * Renders the application with configured routes.
 * Wraps Okta's Secure Route, if not authenticated, it will direct user to Okta login page.
 */
const App = () => {
  const navigate = useNavigate();

  const restoreOriginalUri = useCallback(
    async (_: OktaAuth, originalUri?: string) => {
      await Promise.resolve(
        navigate(
          toRelativeUrl(
            originalUri === undefined ? '/' : originalUri,
            window.location.origin
          ),
          { replace: true }
        )
      );
    },
    [navigate]
  );

  return (
    <Security
      oktaAuth={oktaAuthService}
      restoreOriginalUri={restoreOriginalUri}
    >
      <ErrorBoundary
        message={ErrorBoundaryMessage()}
        description={errorMessage.errorBoundaryMessageDescription}
      >
        <AppContext>
          <Layout>
            <RoutedAppHeader />
            <Content style={{ paddingBottom: '50px' }}>
              <Row style={{ justifyContent: 'center' }}>
                <Col span={20} style={{ paddingTop: '20px' }}>
                  <Routes>
                    <Route path="/login/callback" element={<LoginError />} />
                    <Route path="" element={<SecureRoutes />}>
                      <Route path={routes.home} element={<Home />} />
                      <Route path={routes.account} element={<Account />} />
                      <Route path={routes.newTeam} element={<TeamEditor />} />
                      <Route path={routes.allTeams} element={<AllTeams />} />
                      <Route
                        path={routes.teamRoster}
                        element={<TeamRoster todayDate={new Date()} />}
                      />
                      <Route
                        path={routes.teamSettings}
                        element={<TeamSettings />}
                      />
                      <Route path={routes.editTeam} element={<TeamEditor />} />
                      <Route
                        path={routes.roster}
                        element={<PersonalRoster />}
                      />
                      <Route
                        path={routes.personnelSearch}
                        element={<PersonnelSearch />}
                      />
                      <Route
                        path={routes.personnel}
                        element={<PersonnelInfo />}
                      />
                      <Route path={routes.feedback} element={<Feedback />} />
                      <Route
                        path={routes.exportData}
                        element={<ExportData />}
                      />
                      <Route
                        path={routes.dailyRoster}
                        element={<DailyRosterViewer />}
                      />
                    </Route>
                  </Routes>
                </Col>
              </Row>
            </Content>
          </Layout>
        </AppContext>
      </ErrorBoundary>
    </Security>
  );
};

export default App;
